import { useCallback, useEffect, useRef, useState } from "react";
import {
  UPGRADES,
  SAVE_INTERVAL_MS,
  TICK_MS,
  getCurrentEraKey,
  getTotalProduction,
  getUpgradeCost,
} from "./constants";
import { defaultState, loadState, saveState, clearState } from "./storage";

// Main game hook. Handles:
//  - Loading & persisting state
//  - Berries production tick loop
//  - Manual click actions
//  - Upgrade purchasing (with era-unlock detection)
export const useGame = ({ onEraUnlock } = {}) => {
  const [state, setState] = useState(() => loadState());
  const stateRef = useRef(state);
  stateRef.current = state;

  // Tick loop for automatic Berry production
  useEffect(() => {
    const interval = setInterval(() => {
      setState((prev) => {
        const rate = getTotalProduction(prev.upgrades);
        if (rate === 0) return prev;
        const gain = rate * (TICK_MS / 1000);
        const nextBerries = prev.berries + gain;
        return {
          ...prev,
          berries: nextBerries,
          totalBerriesEarned: (prev.totalBerriesEarned || 0) + gain,
          highestBerries: Math.max(prev.highestBerries || 0, nextBerries),
        };
      });
    }, TICK_MS);
    return () => clearInterval(interval);
  }, []);

  // Autosave loop
  useEffect(() => {
    const interval = setInterval(() => {
      saveState(stateRef.current);
    }, SAVE_INTERVAL_MS);
    const onUnload = () => saveState(stateRef.current);
    window.addEventListener("beforeunload", onUnload);
    return () => {
      clearInterval(interval);
      window.removeEventListener("beforeunload", onUnload);
    };
  }, []);

  const clickWheel = useCallback(() => {
    setState((prev) => {
      const nextBerries = prev.berries + prev.perClick;
      return {
        ...prev,
        berries: nextBerries,
        wheelsBuilt: prev.wheelsBuilt + 1,
        totalBerriesEarned: (prev.totalBerriesEarned || 0) + prev.perClick,
        highestBerries: Math.max(prev.highestBerries || 0, nextBerries),
      };
    });
  }, []);

  const buyUpgrade = useCallback(
    (id) => {
      const upgrade = UPGRADES.find((u) => u.id === id);
      if (!upgrade) return false;

      // Read current state via ref (avoid side-effects inside setState updater
      // which React StrictMode double-invokes).
      const prev = stateRef.current;
      const count = prev.upgrades[id] || 0;
      const cost = getUpgradeCost(upgrade.baseCost, count);
      if (prev.berries < cost) return false;

      const nextUpgrades = { ...prev.upgrades, [id]: count + 1 };
      const nextSeen = { ...prev.seenEras };
      const isEraUnlock = !nextSeen[upgrade.eraKey];
      if (isEraUnlock) nextSeen[upgrade.eraKey] = true;

      // Discord modal is a strictly one-time event, gated by a persistent flag.
      const shouldShowDiscord =
        upgrade.unlockDiscord && !prev.discordShown && isEraUnlock;

      const newState = {
        ...prev,
        berries: prev.berries - cost,
        upgrades: nextUpgrades,
        seenEras: nextSeen,
        discordShown: prev.discordShown || shouldShowDiscord,
      };

      // Commit new state and persist immediately so a reload/tab-close cannot
      // roll back the seen/discordShown flags before autosave fires.
      setState(newState);
      stateRef.current = newState;
      saveState(newState);

      if (isEraUnlock && onEraUnlock) onEraUnlock(upgrade);
      return true;
    },
    [onEraUnlock],
  );

  const toggleSound = useCallback(() => {
    setState((prev) => ({ ...prev, soundEnabled: !prev.soundEnabled }));
  }, []);

  const resetGame = useCallback(() => {
    clearState();
    setState(defaultState());
  }, []);

  const production = getTotalProduction(state.upgrades);
  const eraKey = getCurrentEraKey(state.upgrades);

  return {
    state,
    production,
    eraKey,
    clickWheel,
    buyUpgrade,
    toggleSound,
    resetGame,
  };
};
